package br.com.htms.inspeccaocampo.data

import br.com.htms.inspeccaocampo.platform.currentTimeMillis
import br.com.htms.inspeccaocampo.platform.formatDateBr
import br.com.htms.inspeccaocampo.platform.randomUuid

enum class RespostaTernaria(val rotulo: String) {
    SIM("Sim"),
    NAO("Não"),
    NA("N/A")
}

enum class RespostaSimNao(val rotulo: String) {
    SIM("Sim"),
    NAO("Não")
}

data class FotoAnexo(
    val id: String = randomUuid(),
    val path: String,
    val capturadoEm: Long,
    val latitude: Double? = null,
    val longitude: Double? = null,
    /**
     * JPEG em Base64 (sem prefixo data:). Na Web, garante PDF/Excel mesmo se
     * IndexedDB/path falhar após reload — o JSON da inspeção carrega junto.
     */
    val conteudoBase64: String? = null
)

/** Foto exportada no PDF com metadados de seção/resposta. */
data class FotoExportItem(
    val secao: String,
    val resposta: String,
    val foto: FotoAnexo
)

data class ItemVerificacao(
    val id: String,
    val titulo: String,
    val resposta: RespostaTernaria? = null,
    val fotos: List<FotoAnexo> = emptyList(),
    val justificativa: String = "",
    val obrigatorio: Boolean = true,
    val fotoObrigatoriaSeSim: Boolean = true,
    val fotoObrigatoriaSeNao: Boolean = true
)

/**
 * Campo opcional da Inspeção de Campo: nome livre + Sim/Não/N/A + fotos.
 * Exibido como "Item extra 1", "Item extra 2", …
 */
data class ItemCampoPersonalizado(
    val id: String = randomUuid(),
    val titulo: String = "",
    val resposta: RespostaTernaria? = null,
    val justificativa: String = "",
    val fotos: List<FotoAnexo> = emptyList()
) {
    fun temConteudo(): Boolean =
        titulo.isNotBlank() ||
            resposta != null ||
            justificativa.isNotBlank() ||
            fotos.isNotEmpty()

    fun rotuloSequencia(indice: Int): String = "Item extra ${indice + 1}"

    fun textoExportacao(indice: Int): String {
        if (!temConteudo()) return "—"
        val nome = titulo.ifBlank { rotuloSequencia(indice) }
        val resp = resposta?.rotulo ?: "-"
        return buildString {
            append("${rotuloSequencia(indice)} ($nome): $resp")
            if (justificativa.isNotBlank()) append(" — $justificativa")
        }
    }
}

data class SecaoSimNao(
    val resposta: RespostaSimNao? = null,
    val descricao: String = "",
    val fotos: List<FotoAnexo> = emptyList()
) {
    fun textoExportacao(): String {
        val resp = resposta?.rotulo ?: "-"
        return if (resposta == RespostaSimNao.SIM && descricao.isNotBlank()) "$resp — $descricao" else resp
    }
}

data class FormInspecao(
    val tstResponsavel: String = "",
    val dataHorarioMs: Long = currentTimeMillis(),
    val latitude: Double? = null,
    val longitude: Double? = null,
    val localTexto: String = "",
    val obra: String = "",
    val responsavelFrente: String = "",
    val descricaoAtividade: String = "",
    val efetivo: String = "",
    val itens: List<ItemVerificacao> = InspecaoCatalog.itensPadrao(),
    /** Itens extras opcionais: nome livre + Sim/Não/N/A + fotos. */
    val itensExtra: List<ItemCampoPersonalizado> = listOf(ItemCampoPersonalizado()),
    val desviosEncontrados: SecaoSimNao = SecaoSimNao(),
    val acoesCorretivas: SecaoSimNao = SecaoSimNao(),
    /** Campo opcional: texto livre + fotos (Inspeção de Campo Diária). */
    val informacoesAdicionais: String = "",
    val informacoesAdicionaisFotos: List<FotoAnexo> = emptyList(),
    val conclusao: String = "",
    val assinaturaSupervisor: String = "",
    val assinaturaSesmt: String = ""
) {
    fun dataHorarioFormatado(): String = formatDateBr(dataHorarioMs)

    fun linkGoogleMaps(): String? {
        val lat = latitude ?: return null
        val lng = longitude ?: return null
        return "https://maps.google.com/?q=$lat,$lng"
    }

    /** Fotos do PDF/Excel com seção + resposta do item. */
    fun fotosDetalhadasParaExportacao(): List<FotoExportItem> = buildList {
        itens.forEach { item ->
            val resp = item.resposta?.rotulo ?: "—"
            item.fotos.forEachIndexed { index, foto ->
                add(
                    FotoExportItem(
                        secao = "${item.titulo} — Foto ${index + 1}",
                        resposta = resp,
                        foto = foto
                    )
                )
            }
        }
        itensExtra.forEachIndexed { i, item ->
            if (!item.temConteudo()) return@forEachIndexed
            val nome = item.titulo.ifBlank { item.rotuloSequencia(i) }
            val resp = item.resposta?.rotulo ?: "—"
            item.fotos.forEachIndexed { index, foto ->
                add(
                    FotoExportItem(
                        secao = "${item.rotuloSequencia(i)} ($nome) — Foto ${index + 1}",
                        resposta = resp,
                        foto = foto
                    )
                )
            }
        }
        acoesCorretivas.fotos.forEachIndexed { index, foto ->
            add(
                FotoExportItem(
                    secao = "Ações Corretivas — Foto ${index + 1}",
                    resposta = "—",
                    foto = foto
                )
            )
        }
        informacoesAdicionaisFotos.forEachIndexed { index, foto ->
            add(
                FotoExportItem(
                    secao = "Informações adicionais — Foto ${index + 1}",
                    resposta = "—",
                    foto = foto
                )
            )
        }
    }

    fun fotosParaExportacao(): List<Pair<String, FotoAnexo>> =
        fotosDetalhadasParaExportacao().map { it.secao to it.foto }
}

data class InspecaoCompleta(
    val id: Long = 0,
    val criadoEm: Long = currentTimeMillis(),
    val formTipo: FormTipo = FormTipo.INSPECAO_CAMPO,
    val form: FormInspecao = FormInspecao(),
    val relatorioFoto: FormRelatorioFoto? = null,
    val checklistAlojamento: FormChecklistAlojamento? = null,
    val comunicacaoOcorrencias: FormComunicacaoOcorrencias? = null,
    val checklistDiario: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario? = null,
    val analiseVisualNr: br.com.htms.inspeccaocampo.data.nrvisual.FormAnaliseVisualNr? = null,
    /** true após upload bem-sucedido ao Worker (permite reenvio automático offline). */
    val enviadoRemoto: Boolean = false
) {
    val rotuloTipo: String get() = formTipo.rotulo

    fun responsavelResumo(): String = when (formTipo) {
        FormTipo.INSPECAO_CAMPO -> form.tstResponsavel
        FormTipo.CHECKLIST_DIARIO_HTMS,
        FormTipo.CHECKLIST_DIARIO_AXIA ->
            checklistDiario?.preenchidoPor
                ?.ifBlank { checklistDiario.motoristaNome }
                .orEmpty()
        FormTipo.RELATORIO_FOTO_ALOJAMENTO,
        FormTipo.RELATORIO_ALOJAMENTO ->
            relatorioFoto?.responsavelAlojamento
                ?.ifBlank { checklistAlojamento?.inspecionadoPor?.nome.orEmpty() }
                .orEmpty()
        FormTipo.CHECKLIST_ALOJAMENTO,
        FormTipo.CHECKLIST_VETORES ->
            checklistAlojamento?.inspecionadoPor?.nome.orEmpty()
        FormTipo.COMUNICACAO_OCORRENCIAS ->
            comunicacaoOcorrencias?.quemReportando.orEmpty()
        FormTipo.ANALISE_VISUAL_NR ->
            analiseVisualNr?.tstResponsavel.orEmpty()
        FormTipo.REM_SEGURANCA_OBRAS,
        FormTipo.RELATORIO_SEGURANCA,
        FormTipo.ACOMPANHAMENTO_AMBIENTAL,
        FormTipo.EVIDENCIA_TREINAMENTO -> ""
    }

    fun obraResumo(): String = when (formTipo) {
        FormTipo.INSPECAO_CAMPO -> form.obra
        FormTipo.CHECKLIST_DIARIO_HTMS,
        FormTipo.CHECKLIST_DIARIO_AXIA -> checklistDiario?.obra.orEmpty()
        FormTipo.RELATORIO_FOTO_ALOJAMENTO,
        FormTipo.RELATORIO_ALOJAMENTO ->
            relatorioFoto?.obra?.ifBlank { checklistAlojamento?.obra.orEmpty() }.orEmpty()
        FormTipo.CHECKLIST_ALOJAMENTO,
        FormTipo.CHECKLIST_VETORES -> checklistAlojamento?.obra.orEmpty()
        FormTipo.COMUNICACAO_OCORRENCIAS -> comunicacaoOcorrencias?.obra.orEmpty()
        FormTipo.ANALISE_VISUAL_NR -> analiseVisualNr?.obra.orEmpty()
        FormTipo.REM_SEGURANCA_OBRAS,
        FormTipo.RELATORIO_SEGURANCA,
        FormTipo.ACOMPANHAMENTO_AMBIENTAL,
        FormTipo.EVIDENCIA_TREINAMENTO -> ""
    }

    fun localResumo(): String = when (formTipo) {
        FormTipo.INSPECAO_CAMPO -> form.localTexto
        FormTipo.CHECKLIST_DIARIO_HTMS,
        FormTipo.CHECKLIST_DIARIO_AXIA -> checklistDiario?.localTexto.orEmpty()
        FormTipo.RELATORIO_FOTO_ALOJAMENTO,
        FormTipo.RELATORIO_ALOJAMENTO ->
            relatorioFoto?.endereco?.ifBlank { relatorioFoto.localTexto }
                ?.ifBlank {
                    checklistAlojamento?.endereco?.ifBlank { checklistAlojamento.localTexto }.orEmpty()
                }
                .orEmpty()
        FormTipo.CHECKLIST_ALOJAMENTO,
        FormTipo.CHECKLIST_VETORES ->
            checklistAlojamento?.endereco?.ifBlank { checklistAlojamento.localTexto }.orEmpty()
        FormTipo.COMUNICACAO_OCORRENCIAS ->
            comunicacaoOcorrencias?.localExato
                ?.ifBlank { comunicacaoOcorrencias.localAcidente }
                ?.ifBlank { comunicacaoOcorrencias.localTexto }
                .orEmpty()
        FormTipo.ANALISE_VISUAL_NR -> analiseVisualNr?.localTexto.orEmpty()
        FormTipo.REM_SEGURANCA_OBRAS,
        FormTipo.RELATORIO_SEGURANCA,
        FormTipo.ACOMPANHAMENTO_AMBIENTAL,
        FormTipo.EVIDENCIA_TREINAMENTO -> ""
    }

    fun fotosAnexadas(): List<FotoAnexo> = buildList {
        addAll(form.itens.flatMap { it.fotos })
        addAll(form.itensExtra.flatMap { it.fotos })
        addAll(form.desviosEncontrados.fotos)
        addAll(form.acoesCorretivas.fotos)
        addAll(form.informacoesAdicionaisFotos)
        relatorioFoto?.let { rel ->
            addAll(rel.itens.flatMap { it.fotos })
            addAll(rel.observacoesFotos)
        }
        comunicacaoOcorrencias?.let { addAll(it.fotos) }
        checklistDiario?.let { cd ->
            addAll(cd.fotosPlanoManutencao)
            addAll(cd.fotosEvidenciasManutencao)
            addAll(cd.fotosCrlv)
            addAll(cd.fotosObservacoes)
            addAll(cd.itens.flatMap { item -> item.fotos })
        }
        analiseVisualNr?.let { addAll(it.fotos) }
        checklistAlojamento?.let { chk -> addAll(chk.itens.flatMap { it.fotos }) }
    }

    fun dataHorarioMsResumo(): Long = when (formTipo) {
        FormTipo.INSPECAO_CAMPO -> form.dataHorarioMs
        FormTipo.CHECKLIST_DIARIO_HTMS,
        FormTipo.CHECKLIST_DIARIO_AXIA ->
            checklistDiario?.dataHorarioMs ?: criadoEm
        FormTipo.RELATORIO_FOTO_ALOJAMENTO ->
            relatorioFoto?.dataHorarioMs ?: criadoEm
        FormTipo.CHECKLIST_ALOJAMENTO,
        FormTipo.CHECKLIST_VETORES ->
            checklistAlojamento?.dataHorarioMs ?: criadoEm
        FormTipo.RELATORIO_ALOJAMENTO ->
            relatorioFoto?.dataHorarioMs
                ?: checklistAlojamento?.dataHorarioMs
                ?: criadoEm
        FormTipo.COMUNICACAO_OCORRENCIAS ->
            comunicacaoOcorrencias?.dataHorarioMs ?: criadoEm
        FormTipo.ANALISE_VISUAL_NR ->
            analiseVisualNr?.dataHorarioMs ?: criadoEm
        FormTipo.REM_SEGURANCA_OBRAS,
        FormTipo.RELATORIO_SEGURANCA,
        FormTipo.ACOMPANHAMENTO_AMBIENTAL,
        FormTipo.EVIDENCIA_TREINAMENTO -> criadoEm
    }
}

/** Remove JPEG Base64 embutido; mantém path/id/gps das fotos (gravadas à parte no IDB). */
fun FormInspecao.semConteudoBase64(): FormInspecao = copy(
    itens = itens.map { item ->
        item.copy(fotos = item.fotos.map { it.copy(conteudoBase64 = null) })
    },
    itensExtra = itensExtra.map { item ->
        item.copy(fotos = item.fotos.map { it.copy(conteudoBase64 = null) })
    },
    desviosEncontrados = desviosEncontrados.copy(
        fotos = desviosEncontrados.fotos.map { it.copy(conteudoBase64 = null) }
    ),
    acoesCorretivas = acoesCorretivas.copy(
        fotos = acoesCorretivas.fotos.map { it.copy(conteudoBase64 = null) }
    ),
    informacoesAdicionaisFotos = informacoesAdicionaisFotos.map { it.copy(conteudoBase64 = null) }
)
